import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, CheckCircle2, Gem, Search, X } from 'lucide-react'
import AnimatedButton from '../../../components/AnimatedButton'

const MIN_BET = 100

const DoubleBetModal = ({
  isOpen,
  color,
  isLoading,
  balance,
  inventory,
  onClose,
  onLoadBetForm,
  onSubmit
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [crystalValue, setCrystalValue] = useState('')
  const [selectedItems, setSelectedItems] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [errorText, setErrorText] = useState('')

  useEffect(() => {
    if (isOpen) {
      onLoadBetForm?.()
    }
  }, [isOpen, onLoadBetForm])

  useEffect(() => {
    if (!isOpen) {
      setCrystalValue('')
      setSelectedItems([])
      setSearchQuery('')
      setSortOrder('desc')
      setErrorText('')
    }
  }, [isOpen])

  const parsedCrystal = Number(crystalValue || 0)

  const selectedInventory = useMemo(() => {
    return (inventory || []).filter((item) => selectedItems.includes(item.id))
  }, [inventory, selectedItems])

  const selectedItemsTotal = useMemo(() => {
    return selectedInventory.reduce((sum, item) => sum + Number(item.cost || 0), 0)
  }, [selectedInventory])

  const totalBet = parsedCrystal + selectedItemsTotal
  const canSubmit = totalBet >= MIN_BET

  const filteredInventory = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase()
    const list = normalizedQuery
      ? (inventory || []).filter((item) => String(item.name || '').toLowerCase().includes(normalizedQuery))
      : (inventory || [])

    return [...list].sort((a, b) => {
      const priceA = Number(a.cost || 0)
      const priceB = Number(b.cost || 0)
      return sortOrder === 'asc' ? priceA - priceB : priceB - priceA
    })
  }, [inventory, searchQuery, sortOrder])

  const normalizeCrystal = (value) => {
    if (value === '' || value === null || typeof value === 'undefined') return ''
    const numeric = Number(value)
    const maxBalance = Math.max(0, Number(balance || 0))
    if (!Number.isFinite(numeric)) return ''
    return String(Math.max(0, Math.min(numeric, maxBalance)))
  }

  const toggleItem = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleQuickCrystal = (value) => {
    setCrystalValue(normalizeCrystal(value))
  }

  const handleSubmit = () => {
    setErrorText('')

    if (!color) {
      setErrorText(t('double.bet.errorColor', 'Не выбран цвет ставки'))
      return
    }

    if (!parsedCrystal && selectedItems.length === 0) {
      setErrorText(t('double.bet.errorEmpty', 'Добавьте кристаллы или предметы'))
      return
    }

    if (totalBet < MIN_BET) {
      setErrorText(t('double.bet.errorMin', 'Минимальная ставка 100 кристаллов'))
      return
    }

    onSubmit?.({
      color,
      crystal: parsedCrystal,
      items: selectedItems
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-dark-primary">
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div>
            <h3 className="text-lg font-semibold text-white">{t('double.bet.title', 'Новая ставка')}</h3>
            <p className="text-xs text-text-secondary mt-1">
              {t('double.bet.colorLabel', 'Цвет')}: <b className="text-white">{color}</b>
            </p>
          </div>

          <button onClick={onClose} className="p-2 text-text-secondary hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="rounded-xl border border-white/10 bg-surface/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-semibold">{t('double.bet.crystals', 'Кристаллы')}</h4>
              <p className="inline-flex items-center gap-1 text-sm text-text-secondary">
                <Gem className="w-4 h-4 text-accent-primary" />
                <span className="text-white font-semibold">{Number(balance || 0).toLocaleString()}</span>
              </p>
            </div>

            {Number(balance || 0) > 0 ? (
              <>
                <input
                  type="number"
                  min="0"
                  value={crystalValue}
                  onChange={(event) => setCrystalValue(normalizeCrystal(event.target.value))}
                  placeholder={t('double.bet.enterCrystals', 'Введите сумму')}
                  className="w-full rounded-lg border border-white/10 bg-dark-primary px-3 py-2 text-white outline-none"
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  {[100, 500, 1000, 5000].map((value) => (
                    <button
                      key={value}
                      onClick={() => handleQuickCrystal(parsedCrystal + value)}
                      className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-text-secondary hover:text-white"
                    >
                      +{value}
                    </button>
                  ))}

                  {[0.25, 0.5, 0.75, 1].map((ratio) => (
                    <button
                      key={ratio}
                      onClick={() => handleQuickCrystal(Math.floor(Number(balance || 0) * ratio))}
                      className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm text-text-secondary hover:text-white"
                    >
                      {ratio === 1 ? t('double.bet.max', 'MAX') : `${ratio * 100}%`}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <button
                onClick={() => navigate('/payment')}
                className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-text-secondary hover:text-white"
              >
                {t('double.bet.topUp', 'Пополнить баланс')}
              </button>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-surface/40 p-4">
            <div className="flex flex-col gap-3 mb-3">
              <h4 className="text-white font-semibold">{t('double.bet.items', 'Предметы')}</h4>

              {(inventory || []).length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
                    <input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={t('double.bet.search', 'Поиск по названию')}
                      className="w-full rounded-lg border border-white/10 bg-dark-primary py-2 pl-9 pr-3 text-white outline-none"
                    />
                  </div>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="rounded-lg border border-white/10 bg-dark-primary px-3 py-2 text-text-secondary hover:text-white"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {(inventory || []).length === 0 ? (
              <p className="text-sm text-text-secondary">{t('double.bet.emptyItems', 'Нет доступных предметов')}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 md:grid-cols-5">
                {filteredInventory.map((item) => {
                  const isSelected = selectedItems.includes(item.id)
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      className={`relative rounded-lg border p-2 transition-all ${
                        isSelected
                          ? 'border-accent-primary shadow-[0_0_20px_rgba(0,255,127,0.2)]'
                          : 'border-white/10 bg-dark-primary/60 hover:border-white/20'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute left-1 top-1 z-10 rounded-full bg-accent-primary p-0.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-dark-primary" />
                        </div>
                      )}
                      <div className="aspect-square flex items-center justify-center">
                        <img src={item.img} alt={item.name} className="w-full h-full object-contain" loading="lazy" />
                      </div>
                      <p className="mt-1 truncate text-[11px] text-white">{item.name}</p>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 p-4">
          {errorText && <p className="mb-2 text-sm text-red-400">{errorText}</p>}
          <AnimatedButton onClick={handleSubmit} disabled={isLoading || !canSubmit}>
            {isLoading
              ? t('double.bet.sending', 'Отправка...')
              : t('double.bet.submit', 'Сделать ставку')}
            <span className="ml-2 inline-flex items-center gap-1">
              {totalBet.toLocaleString()} <Gem className="w-4 h-4 text-dark-primary" />
            </span>
          </AnimatedButton>
        </div>
      </div>
    </div>
  )
}

export default DoubleBetModal
